import {createSelector} from 'reselect'
import keyBy from 'lodash/keyBy.js'
import orderBy from 'lodash/orderBy.js'
import find from 'lodash/find.js'
import reduce from 'lodash/reduce.js'
import get from 'lodash/get.js'
import debounce from 'lodash/debounce.js'
import isBefore from 'date-fns/isBefore'

import {
  AMAZON_SFP,
  CANADA_POST,
  ENDICIA,
  PITNEY,
  PITNEY_MERCHANT,
  USPS_SHIPPERS,
  UPS,
  FEDEX,
  USPS_SHIP,
  FEDEX_360,
  MULTI_CARRIER_SHIPPER,
} from '../common/constants/ShipperNames.js'
import {fetchAllAPI} from '../common/fetchAll.js'
import apiverson from '../common/apiverson.js'
import {shipperBalancesSelector} from './shipperBalances.js'
import {
  canUsePitneySelector,
  currencySymbolSelector,
  useOrdoroShippersSelector,
} from '../data/company.js'
import {accountBalanceSelector} from './account.js'
import {getState, setForm, updateForm, formsSelector} from '../store.js'
import {defaultWarehouseSelector} from './warehouses.js'
import {getNounsFromValue, tokenizeOptions} from '../common/tokenizeOptions.js'
import {
  ERROR_TYPE__FINISH_SETUP,
  ERROR_TYPE__NEEDS_REAUTH,
} from '../common/constants/index.js'
import {buildPath} from '../common/querystring.js'
import {shippingMethodOptionsSelector} from './shipperOptions.js'

export const SHIPPERS = 'shippers'

export function setShippers(shippers) {
  setForm(SHIPPERS, keyBy(shippers, 'id'))
}

export function setShipper(shipper) {
  updateForm(SHIPPERS, {[shipper.id]: shipper})
}

export async function getShippers() {
  const shippers = await fetchAllAPI('/shipper', 'shipper', {all: true})

  setShippers(shippers)

  await getMultiCarriers()
}

export const getShippersDebounced = debounce(getShippers, 500)

export const shippersSelector = createSelector(
  formsSelector,
  (forms) => forms[SHIPPERS] || shippersSelector.default,
)
shippersSelector.default = {}

export function shippersHaveLoadedSelector(state) {
  return !!formsSelector(state)[SHIPPERS]
}

export function shipperSelector(state, {shipperID}) {
  return shippersSelector(state)[shipperID]
}

export function getShipperName(shipper) {
  return shipper ? shipper.name : ''
}

export function getMultiCarrierName(shipper, carrierAccountID) {
  return (
    shipper?.carrier_account_options_by_value?.[carrierAccountID]?.display ||
    getShipperName(shipper)
  )
}

export function shipperNameSelector(state, {shipperID}) {
  return getShipperName(shipperSelector(state, {shipperID}))
}

export function isArchivedSelector(state, {shipperID}) {
  return !!get(shipperSelector(state, {shipperID}), ['archive_date'])
}

function getCanArchive(shipper) {
  if (!shipper) {
    return false
  }

  if (shipper.vendor !== USPS_SHIP && USPS_SHIPPERS.includes(shipper.vendor)) {
    return false
  }

  return true
}

export function canArchiveSelector(state, {shipperID}) {
  return getCanArchive(shipperSelector(state, {shipperID}))
}

export function shipperTypeSelector(state, {shipperID}) {
  const shipper = shipperSelector(state, {shipperID})

  return shipper ? shipper.vendor : null
}

export const shippersSortedByIDSelector = createSelector(
  shippersSelector,
  (shippers) =>
    orderBy(
      shippers,
      ['archive_date', 'index', 'id'],
      ['desc', 'asc', 'asc'],
    ).filter(
      // non-archived shippers OR (archived shippers that "can be archived", thus can be un-archived)
      (shipper) => !shipper.archive_date || getCanArchive(shipper),
    ),
)

export const activeShippersSelector = createSelector(
  shippersSortedByIDSelector,
  (shippers) => shippers.filter((shipper) => !shipper.archive_date),
)

export const activeShipperIDsSelector = createSelector(
  activeShippersSelector,
  (activeShippers) => activeShippers.map((shipper) => shipper.id),
)

export const approvedShippersSelector = createSelector(
  activeShippersSelector,
  (shippers) => shippers,
)

export const shipperOptionsSelector = createSelector(
  approvedShippersSelector,
  (shippers) =>
    shippers.map((shipper) => ({
      value: shipper.id,
      display: shipper.name,
      entity: shipper,
      type: SHIPPERS,
      nouns: [...getNounsFromValue(shipper.name), shipper.vendor],
    })),
)

export const shipperOptionTokensSelector = createSelector(
  shipperOptionsSelector,
  (shipperOptions) => tokenizeOptions(shipperOptions),
)

export const needsShipperSelector = createSelector(
  shippersSelector,
  shippersHaveLoadedSelector,
  (shippers, shippersHaveLoaded) =>
    shippersHaveLoaded && Object.keys(shippers).length === 0,
)

export const needsUSPSShipperSelector = createSelector(
  shippersSelector,
  useOrdoroShippersSelector,
  canUsePitneySelector,
  (shippers, useOrdoroShippers, canUsePitney) =>
    canUsePitney &&
    useOrdoroShippers &&
    !find(
      shippers,
      (shipper) =>
        shipper.archive_date === null &&
        [ENDICIA, PITNEY, PITNEY_MERCHANT, USPS_SHIP].includes(shipper.vendor),
    ),
)

export const hasAmazonSFPSelector = createSelector(
  shippersSelector,
  shippersHaveLoadedSelector,
  (shippers, hasLoadedShippers) => {
    if (!hasLoadedShippers) {
      return true
    }

    return !!find(shippers, (shipper) => shipper.vendor === AMAZON_SFP)
  },
)

export const hasChargeableShipperAccountSelector = createSelector(
  shippersSelector,
  (shippers) =>
    reduce(
      shippers,
      (prev, {vendor}) => prev || [PITNEY].includes(vendor),
      false,
    ),
)

export function balanceSelector(state, {shipperID}) {
  const shipperType = shipperTypeSelector(state, {shipperID})
  const shipperBalances = shipperBalancesSelector(state)
  const accountBalance = accountBalanceSelector(state, {shipperID})

  if (shipperType === ENDICIA) {
    return (
      shipperBalances[shipperID] && shipperBalances[shipperID].postage_balance
    )
  }
  if (shipperType === PITNEY_MERCHANT) {
    return shipperBalances[shipperID] && shipperBalances[shipperID].balance
  }
  if ([PITNEY].includes(shipperType)) {
    return accountBalance
  }
  return null
}

export function getCanAddPostage(shipperType) {
  return [ENDICIA, PITNEY].includes(shipperType)
}

export function autoFillPostageConfigSelector(state, {shipperID}) {
  const shipper = shipperSelector(state, {shipperID})

  const config =
    shipper && shipper.autorefill_config ? shipper.autorefill_config : {}

  return {
    isEnabled: config.enabled || false,
    lowerThreshold: config.lower_threshold || 0,
    incrementAmount: config.increment_amount || 0,
  }
}

export const hasCanadaPostShipperSelector = createSelector(
  shippersSelector,
  (shippers) => !!find(shippers, {vendor: CANADA_POST}),
)

export function defaultFedExSmartPostHubSelector(state, {shipperID}) {
  const shipper = shipperSelector(state, {shipperID})

  return get(shipper, 'vendor_config.smartpost_hub') || null
}

export function getShipperCurrencySymbol(shipperType, currencySymbol) {
  if ([ENDICIA, PITNEY, PITNEY_MERCHANT].includes(shipperType)) {
    return '$'
  }

  return currencySymbol
}

export function shipperCurrencySymbolSelector(state, {shipperID, shipperType}) {
  shipperType = shipperType || shipperTypeSelector(state, {shipperID})
  const currencySymbol = currencySymbolSelector(state)

  return getShipperCurrencySymbol(shipperType, currencySymbol)
}

export function canAutoCreatePitneyShipperSelector(state) {
  // has fully setup US default warehouse
  const warehouse = defaultWarehouseSelector(state)

  if (
    !warehouse ||
    !warehouse.is_configured_for_shipping ||
    warehouse.address.country !== 'US'
  ) {
    return false
  }

  // has no USPS shipper
  return needsUSPSShipperSelector(state)
}

export async function autoCreatePitneyShipper() {
  if (!canAutoCreatePitneyShipperSelector(getState())) {
    return
  }

  await apiverson.post('/carrier/pitney')

  await getShippers()
}

export function usesDiscountedRatesSelector(state, {shipperID}) {
  const shipper = shipperSelector(state, {shipperID})

  return get(shipper, 'vendor_config.uses_discounted_rates') || false
}

export function upsTokenExpirationSelector(state, {shipperID}) {
  const shipper = shipperSelector(state, {shipperID})

  if (shipper?.vendor !== UPS) {
    return null
  }

  const tokenExpiration =
    shipper.vendor_config.refresh_token_expires ||
    (!shipper.vendor_config.refresh_token && shipper.vendor_config.expires) ||
    null

  const isExpired = tokenExpiration
    ? isBefore(new Date(tokenExpiration), new Date())
    : true

  return {tokenExpiration, isExpired}
}

export function fedExInitialAuthNeededSelector(state, {shipperID}) {
  const shipper = shipperSelector(state, {shipperID})
  if (shipper?.vendor !== FEDEX) {
    return null
  }

  // once they have a child_key then they are forever authorized as far as we can tell
  // otherwise force "right now" date for expiration
  const tokenExpiration = shipper.vendor_config.child_key
    ? null
    : new Date().toISOString()

  return {tokenExpiration, isExpired: !!tokenExpiration}
}

export function uspsShipIntegrationIssueSelector(state, {shipperID}) {
  const shipper = shipperSelector(state, {shipperID})

  if (shipper?.vendor !== USPS_SHIP) {
    return null
  }

  const {vendor_config} = shipper

  const tokenExpiration =
    vendor_config.refresh_token_expires ||
    (!vendor_config.refresh_token && vendor_config.access_token_expires) ||
    null

  const isExpired = tokenExpiration
    ? isBefore(new Date(tokenExpiration), new Date())
    : true

  const issue = {tokenExpiration, isExpired}

  const needsToFinishSetup =
    !vendor_config.merchant_crid ||
    !vendor_config.merchant_outbound_mid ||
    !vendor_config.merchant_manifest_mid ||
    !vendor_config.merchant_eps

  if (needsToFinishSetup) {
    issue.integrationIssue = {
      message: 'needs credentials',
      errorType: ERROR_TYPE__FINISH_SETUP,
    }
  }

  return issue
}

export function fedEx360IntegrationIssueSelector(state, {shipperID}) {
  const shipper = shipperSelector(state, {shipperID})

  if (shipper?.vendor !== FEDEX_360) {
    return null
  }

  const {vendor_config} = shipper

  const hasAProblem =
    !!vendor_config.carrier_account_id.match(/[@]/) ||
    !vendor_config.carrier_account_id ||
    !vendor_config.developer_partner_id ||
    !vendor_config.shipper_id

  if (!hasAProblem) {
    return null
  }

  return {
    integrationIssue: {
      message: 'needs credentials',
      errorType: ERROR_TYPE__FINISH_SETUP,
    },
  }
}

export function tokenExpirationSelector(state, {shipperID}) {
  return (
    upsTokenExpirationSelector(state, {shipperID}) ||
    fedExInitialAuthNeededSelector(state, {shipperID}) ||
    uspsShipIntegrationIssueSelector(state, {shipperID}) ||
    fedEx360IntegrationIssueSelector(state, {shipperID}) || {
      tokenExpiration: null,
      isExpired: false,
    }
  )
}

export function shipperIntegrationIssuesSelector(state) {
  const shippers = activeShippersSelector(state)

  return shippers
    .map((shipper) => ({
      shipper,
      ...tokenExpirationSelector(state, {shipperID: shipper.id}),
    }))
    .filter(({isExpired, integrationIssue}) => isExpired || integrationIssue)
    .map(({shipper, integrationIssue}) => ({
      id: shipper.id,
      type: 'shipper',
      name: shipper.name,
      message: 'needs to be reauthorized',
      errorType: ERROR_TYPE__NEEDS_REAUTH,
      link: `#/settings/shipper/${shipper.id}/edit`,
      ...integrationIssue,
    }))
}

export async function getMultiCarriers() {
  const shippers = shippersSortedByIDSelector(getState()).filter(
    ({vendor, archive_date}) =>
      MULTI_CARRIER_SHIPPER.includes(vendor) && !archive_date,
  )

  for (const shipper of shippers) {
    const {json} = await apiverson.get(
      buildPath(['shipper', shipper.id, `${shipper.vendor}_carriers`], {
        shipping_methods: true,
      }),
    )

    const carrier_account_options = []
    const shippingMethodOptionsMap = new Map()
    for (const carrier of json) {
      carrier_account_options.push({
        value: carrier.carrier_account_id,
        display: carrier.display_name,
        shipping_methods: carrier.shipping_method,
        carrier: carrier.vendor,
        active: carrier.active,
      })

      if (carrier.shipping_method) {
        for (const option of carrier.shipping_method) {
          shippingMethodOptionsMap.set(option.value, {
            ...option,
            carrier: carrier.vendor,
          })
        }
      }
    }

    const shipping_method_options = [...shippingMethodOptionsMap.values()]

    setShipper({
      ...shipper,
      carrier_account_options,
      carrier_account_options_by_value: keyBy(carrier_account_options, 'value'),
      shipping_method_options,
      shipping_method_options_by_value: keyBy(shipping_method_options, 'value'),
    })
  }

  if (shippers.length > 0) {
    if (getMultiCarriers.timeout) {
      // if it was already scheduled, then cancel that one
      clearTimeout(getMultiCarriers.timeout)
    }

    // we need to periodically ask for shippers because the shipper's carriers might have changed
    getMultiCarriers.timeout = setTimeout(getShippersDebounced, 60 * 60 * 1000)
  }
}
getMultiCarriers.timeout = null

export function shipperCarrierOptionsSelector(state, {shipperID}) {
  const shipper = shipperSelector(state, {shipperID})

  if (!shipper) {
    return shipperCarrierOptionsSelector.default
  }

  if (shipper.carrier_account_options?.length > 0) {
    return shipper.carrier_account_options
  }

  return []
}
shipperCarrierOptionsSelector.default = []

export function shipperShippingMethodOptionsSelector(state, {shipperID}) {
  const shipper = shipperSelector(state, {shipperID})

  if (!shipper) {
    return shipperShippingMethodOptionsSelector.default
  }

  if (shipper.shipping_method_options?.length > 0) {
    return shipper.shipping_method_options
  }

  return shippingMethodOptionsSelector(state, {shipperType: shipper.vendor})
}
shipperShippingMethodOptionsSelector.default = []

export function shippingMethodDisplayByShipperTypeByShippingMethodSelector(
  state,
) {
  const shippers = shippersSortedByIDSelector(state)

  const lookup = {}
  for (const shipper of shippers) {
    let shippingMethodLookup = lookup[shipper.vendor]
    if (!shippingMethodLookup) {
      shippingMethodLookup = lookup[shipper.vendor] = {}
    }

    const options = shipperShippingMethodOptionsSelector(state, {
      shipperID: shipper.id,
    })

    for (const option of options) {
      shippingMethodLookup[option.value] = option.display
    }
  }

  return lookup
}

export function shipperShippingMethodSelector(state, {shipperID, methodCode}) {
  const shippingMethods = shipperShippingMethodOptionsSelector(state, {
    shipperID,
  })

  return shippingMethods.find(({value}) => methodCode === value)
}

export function shipperShippingMethodNameSelector(
  state,
  {shipperID, methodCode},
) {
  const method = shipperShippingMethodSelector(state, {shipperID, methodCode})

  return method ? method.display : methodCode
}
